import React from 'react';
import './MenuStateIllustration.css';

/**
 * Restaurant-themed artwork for the Menu page's empty / no-results / error states.
 * Drawn with theme colour tokens (see the CSS) so it follows each store's brand colour
 * and the dark theme.
 *
 *  - "empty":     a covered dish (cloche) with sparkles — the menu is on its way
 *  - "noResults": an empty plate between fork and knife, under a magnifier that finds nothing
 *  - "error":     a cloche with a warning badge — the menu couldn't be served
 */
export default function MenuStateIllustration({ variant = 'empty', className = '' }) {
    return (
        <svg
            className={`menu-state-art menu-state-art--${variant} ${className}`}
            viewBox="0 0 160 140"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
        >
            <circle className="msa-backdrop" cx="80" cy="72" r="56" />
            {/* Ground shadow only under the covered dish; the flat plate already sits on the backdrop. */}
            {variant !== 'noResults' && <ellipse className="msa-shadow" cx="80" cy="121" rx="44" ry="4.5" />}

            {variant === 'noResults' ? <NoResultsScene /> : <ClocheScene variant={variant} />}
        </svg>
    );
}

function ClocheScene({ variant }) {
    return (
        <>
            {/* Plate */}
            <path className="msa-plate" d="M30 108 H130 Q127 116 118 116 H42 Q33 116 30 108 Z" />

            {/* Dome + rim + knob */}
            <path className="msa-dome" d="M38 104 Q38 60 80 58 Q122 60 122 104 Z" />
            <path className="msa-dome-shine" d="M54 92 Q55 74 70 68" />
            <rect className="msa-rim" x="32" y="102" width="96" height="7" rx="3.5" />
            <circle className="msa-knob" cx="80" cy="53" r="6" />

            {variant === 'error' ? (
                /* Warning badge */
                <g className="msa-badge">
                    <circle cx="118" cy="44" r="13" />
                    <path className="msa-badge-mark" d="M118 37 V46 M118 51 V51.5" />
                </g>
            ) : (
                /* Sparkles: something tasty is coming */
                <>
                    <path className="msa-sparkle msa-sparkle--a" d="M80 18 Q81.6 26.4 90 28 Q81.6 29.6 80 38 Q78.4 29.6 70 28 Q78.4 26.4 80 18 Z" />
                    <path className="msa-sparkle msa-sparkle--b" d="M53 33 Q54 38 59 39 Q54 40 53 45 Q52 40 47 39 Q52 38 53 33 Z" />
                    <path className="msa-sparkle msa-sparkle--c" d="M108 31 Q108.8 35.2 113 36 Q108.8 36.8 108 41 Q107.2 36.8 103 36 Q107.2 35.2 108 31 Z" />
                </>
            )}
        </>
    );
}

function NoResultsScene() {
    return (
        <>
            {/* Fork */}
            <path className="msa-cutlery" d="M26 46 V64 M32 46 V64 M38 46 V64 M26 64 Q32 72 38 64 M32 70 V112" />
            {/* Knife */}
            <path className="msa-cutlery" d="M124 112 V46 Q136 54 132 82 H124" />

            {/* Empty plate */}
            <circle className="msa-plate-outer" cx="78" cy="80" r="34" />
            <circle className="msa-plate-inner" cx="78" cy="80" r="23" />

            {/* Magnifier that finds nothing */}
            <g className="msa-magnifier">
                <path className="msa-handle" d="M107 103 L119 115" />
                <circle className="msa-lens" cx="96" cy="92" r="15" />
                <path className="msa-cross" d="M90.5 86.5 L101.5 97.5 M101.5 86.5 L90.5 97.5" />
            </g>
        </>
    );
}
